import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import { ASSETS } from "@/lib/assets";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Send, X, ChevronLeft, Loader, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function WorldMap() {
  const { state, refreshState } = useAuth();
  const navigate = useNavigate();
  const [world, setWorld] = useState(null);
  const [selected, setSelected] = useState(null);
  const [zombies, setZombies] = useState(50);
  const [busy, setBusy] = useState(false);
  const [verdict, setVerdict] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [strategyPlan, setStrategyPlan] = useState(null);

  const fetchWorld = useCallback(async () => {
    try {
      const { data } = await api.get("/world/state");
      setWorld(data);
      if (data.king_counter_event) {
        const e = data.king_counter_event;
        toast.error(
          e.retaken
            ? `${e.region_name} RETAKEN by humans. ${e.zombies_lost} zombies lost.`
            : `${e.region_name} attacked. ${e.zombies_lost} fell. Held the line.`
        );
      }
      if (data.prince_event) {
        const p = data.prince_event;
        if (p.raided_region) {
          toast.error(
            p.raid_captured
              ? `${p.prince_name} STORMED ${p.raided_region} — your garrison of ${p.zombies_lost.toLocaleString()} is gone.`
              : `${p.prince_name} raided ${p.raided_region}. ${p.zombies_lost.toLocaleString()} of your dead fell. The region held.`,
            { duration: 8000 }
          );
        } else {
          toast.warning(
            p.captured_region
              ? `${p.prince_name} SEIZED ${p.captured_region}. Their horde swells to ${p.new_horde.toLocaleString()}.`
              : `${p.prince_name} grows stronger. +${p.growth.toLocaleString()} zombies (${p.new_horde.toLocaleString()} total).`,
            { duration: 8000 }
          );
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "World map locked.");
      navigate("/command");
    }
  }, [navigate]);

  const submitSuggestion = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post("/world/suggest", {
        region_key: selected.key,
        zombies_proposed: zombies,
        rationale: "",
      });
      setVerdict({ verdict: data.verdict, is_suggestion: true,
                   superior_accepts: data.logged.accepted });
      if (data.logged.accepted) toast.success("Superior accepted the suggestion.");
      else toast.error("Superior dismissed your suggestion.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suggestion failed.");
    } finally {
      setBusy(false);
    }
  };

  const suggestStrategy = async () => {
    setSuggesting(true);
    setStrategyPlan(null);
    try {
      const { data } = await api.post("/world/suggest_strategy");
      const plan = data.plan;
      const region = world?.regions.find((r) => r.key === plan.region_key);
      if (region) setSelected(region);
      setZombies(plan.zombies_to_send);
      setStrategyPlan(plan);
      toast.success("The Quartermaster speaks.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No strategy available.");
    } finally {
      setSuggesting(false);
    }
  };

  useEffect(() => { fetchWorld(); /* eslint-disable-next-line */ }, []);

  const attack = async () => {
    if (!selected) return;
    setBusy(true);
    setVerdict(null);
    try {
      const { data } = await api.post("/world/attack", {
        region_key: selected.key,
        zombies_to_send: zombies,
      });
      setVerdict(data);
      await fetchWorld();
      await refreshState();
      if (data.is_suggestion && !data.superior_accepts) {
        toast.error("Suggestion dismissed.");
      } else if (data.won === false) {
        toast.error("Repulsed.");
      } else {
        toast.success("Order executed.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attack failed.");
    } finally {
      setBusy(false);
    }
  };

  // Project lat/lon → x/y in % over the map image.
  // Using equirectangular-like projection: lon (-180..180) → x (0..100), lat (-90..90) → y (0..100, flipped)
  const projected = useMemo(() => {
    if (!world) return [];
    return world.regions.map((r) => ({
      ...r,
      x: ((r.lon + 180) / 360) * 100,
      y: ((90 - r.lat) / 180) * 100,
    }));
  }, [world]);

  if (!world || !state) {
    return (
      <div>
        <NavBar active="world" />
        <div className="min-h-[80vh] flex items-center justify-center text-bone tac-label">
          MAPPING THE WORLD...
        </div>
      </div>
    );
  }

  const isSuggestionMode = !world.rebellion_started && state.rank_index < 5;
  const baseWeight = world.base_suggestion_weight ?? world.suggestion_weight;
  const effWeight = world.suggestion_weight;
  const reputation = world.reputation ?? 0;
  const suggestionPct = Math.round(effWeight * 100);
  const baseSuggestionPct = Math.round(baseWeight * 100);
  const zombieRegions = world.regions.filter((r) => r.controller === "zombies").length;
  const isKingOrEmperor = state.rank_index >= 5;
  const emperorThreshold = 11;

  return (
    <div className="min-h-screen">
      <NavBar active="world" />

      <div className="max-w-[1500px] mx-auto px-4 sm:px-8 lg:px-12 py-6 lg:py-10">
        <button onClick={() => navigate("/command")} className="btn-ghost mb-4 flex items-center gap-1" data-testid="world-back-button">
          <ChevronLeft className="w-3 h-3" /> COMMAND
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <p className="tac-label" data-testid="world-mode-label">
              {isSuggestionMode ? `SUGGESTION MODE // SUPERIOR LISTENS ${suggestionPct}% OF THE TIME`
                                : "DIRECT COMMAND // YOUR WORD IS LAW"}
            </p>
            {isSuggestionMode && reputation > 0 && (
              <p className="text-[0.65rem] text-blood tracking-widest mt-1" data-testid="reputation-bonus">
                REPUTATION +{Math.round(reputation * 100)}% (BASE {baseSuggestionPct}%)
              </p>
            )}
            <h1 className="font-serif-h text-4xl lg:text-6xl uppercase text-bone leading-none mt-2"
                data-testid="world-heading">
              GLOBAL THEATRE
            </h1>
            <p className="text-xs text-muted-h mt-2 tracking-wider" data-testid="world-pct">
              {Math.round(world.world_pct_zombie * 100)}% OF THE WORLD WALKS DEAD
            </p>

            {world.pending_counter_raid && (
              <CounterRaidPanel
                pending={world.pending_counter_raid}
                maxHorde={world.personal_horde}
                onDone={async () => { await fetchWorld(); await refreshState(); }}
              />
            )}

            <div className="mt-4 flex items-start gap-4 flex-wrap">
              <button onClick={suggestStrategy} disabled={suggesting}
                      className="btn-bone flex items-center gap-2"
                      data-testid="suggest-strategy-button">
                {suggesting
                  ? <><Loader className="w-3 h-3 animate-spin" /> READING THE WIND…</>
                  : <><Sparkles className="w-3 h-3" /> SUGGEST STRATEGY</>}
              </button>
              {strategyPlan && (
                <div className="border-l-2 border-blood pl-3 py-1 max-w-2xl"
                     data-testid="strategy-plan">
                  <p className="tac-label">QUARTERMASTER</p>
                  <p className="text-sm text-[var(--text-main)] italic mt-1 leading-relaxed">
                    "{strategyPlan.rationale}"
                  </p>
                  <p className="text-[0.65rem] text-muted-h font-mono mt-1 tracking-wider">
                    SEND {strategyPlan.zombies_to_send}
                  </p>
                </div>
              )}
            </div>

            {isKingOrEmperor && state.rank_index < 6 && (
              <div className="mt-3 tactical-card" data-testid="emperor-progress-bar">
                <div className="flex items-baseline justify-between mb-2">
                  <span className="tac-label text-blood">PATH TO EMPEROR</span>
                  <span className="font-mono text-xs text-bone">
                    {Math.min(zombieRegions, emperorThreshold)} / {emperorThreshold} REGIONS
                  </span>
                </div>
                <div className="bar-track">
                  <div className="bar-fill"
                       style={{ width: `${Math.min(100, (zombieRegions / emperorThreshold) * 100)}%` }} />
                </div>
                <p className="text-[0.65rem] text-muted-h mt-2 tracking-wider">
                  CONTROL {emperorThreshold} REGIONS TO CLAIM THE TITLE OF EMPEROR
                </p>
              </div>
            )}
            {state.rank_index === 6 && (
              <p className="tac-label text-blood mt-3" data-testid="emperor-status">
                ◆ EMPEROR OF THE DEAD ◆ THE WORLD ENDS WHEN YOU END IT
              </p>
            )}

            {/* MAP */}
            <div className="relative mt-6 border border-subtle bg-void"
                 style={{ aspectRatio: "2 / 1" }} data-testid="world-map-container">
              <img src={ASSETS.worldMap} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
              <div className="absolute inset-0"
                   style={{ background: "radial-gradient(ellipse at center, rgba(3,3,3,0.3) 0%, rgba(3,3,3,0.85) 100%)" }} />

              {projected.map((r) => {
                const isAi = r.controller === "ai_prince";
                const isZ = r.controller === "zombies";
                const isContested = r.controller === "contested";
                const color = r.is_king_fortress ? "#FFD700"
                  : isAi ? "#B30000"
                  : isZ ? "#1B4D3E"
                  : isContested ? "#8A0303"
                  : "#52525B";
                const isSel = selected?.key === r.key;
                return (
                  <button
                    key={r.key}
                    onClick={() => { setSelected(r); setVerdict(null); }}
                    style={{ left: `${r.x}%`, top: `${r.y}%` }}
                    className={`absolute -translate-x-1/2 -translate-y-1/2 ${isSel ? "z-30" : "z-20"}`}
                    data-testid={`region-marker-${r.key}`}
                  >
                    <div
                      className={`w-3 h-3 ${r.controller === "humans" ? "" : "pulse-marker"}`}
                      style={{ background: color, outline: isSel ? "2px solid #EAE6D7" : "1px solid rgba(234,230,215,0.4)" }}
                    />
                    {isSel && (
                      <div className="absolute top-4 left-1/2 -translate-x-1/2 whitespace-nowrap text-[0.6rem] tracking-widest text-bone bg-void/90 border border-blood px-2 py-1">
                        {r.name}
                      </div>
                    )}
                  </button>
                );
              })}

              {/* Legend */}
              <div className="absolute bottom-3 left-3 text-[0.6rem] tracking-widest text-bone bg-void/85 border border-subtle p-3 space-y-1">
                <Legend color="#52525B" label="HUMANS" />
                <Legend color="#8A0303" label="CONTESTED" />
                <Legend color="#1B4D3E" label="ZOMBIES" />
                <Legend color="#B30000" label="AI PRINCE" />
                <Legend color="#FFD700" label="KING'S FORTRESS" />
              </div>
            </div>

            {/* AI Princes */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {(() => {
                // Only trust the ai_princes list when regions still show a
                // matching ai_prince controller. Otherwise the state may
                // have gone stale after a rebellion / reset.
                const activePrinces = (world.ai_princes || []).filter((p) =>
                  (world.regions || []).some((r) => r.controller === "ai_prince" && r.ai_owner === p.id)
                );
                const orphanPrinceRegions = (world.regions || []).filter(
                  (r) => r.controller === "ai_prince" &&
                         !activePrinces.some((p) => p.id === r.ai_owner)
                );
                if (activePrinces.length === 0 && orphanPrinceRegions.length === 0) {
                  return (
                    <p className="col-span-3 text-xs text-muted-h tracking-wider">
                      ALL RIVAL PRINCES ARE DEAD.
                    </p>
                  );
                }
                return (
                  <>
                    {activePrinces.map((p) => (
                      <div key={p.id} className="tactical-card"
                           data-testid={`ai-prince-${p.id}`}>
                        <div className="flex items-baseline justify-between gap-2">
                          <p className="tac-label text-[#B30000]">RIVAL CROWN PRINCE</p>
                          {p.personality_label && (
                            <span className="font-mono text-[0.55rem] tracking-widest border border-blood/60 text-blood px-1.5 py-0.5"
                                  data-testid={`ai-prince-personality-${p.id}`}>
                              {p.personality_label}
                            </span>
                          )}
                        </div>
                        <p className="font-serif-h text-xl text-bone mt-1">{p.name}</p>
                        <p className="font-mono text-xs text-muted-h mt-1">
                          HORDE {p.horde.toLocaleString()}
                        </p>
                        {p.personality_blurb && (
                          <p className="text-[0.65rem] text-muted-h/80 italic mt-1 leading-snug">
                            {p.personality_blurb}
                          </p>
                        )}
                      </div>
                    ))}
                    {orphanPrinceRegions.length > 0 && (
                      <div className="col-span-3 tactical-card border-l-2 border-blood/50">
                        <p className="tac-label text-blood">UNNAMED CONTESTED GROUND</p>
                        <p className="text-xs text-bone mt-1 leading-snug">
                          {orphanPrinceRegions.length} region{orphanPrinceRegions.length > 1 ? "s" : ""}
                          {" "}still held by nameless rival dead ({orphanPrinceRegions.map((r) => r.name).join(", ")}).
                        </p>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          {/* SIDE PANEL */}
          <div className="lg:col-span-4">
            <div className="tactical-card sticky top-4">
              <p className="tac-label">YOUR HORDE</p>
              <p className="font-serif-h text-3xl text-bone" data-testid="world-horde-size">{state.personal_horde}</p>

              {!selected && (
                <p className="text-xs text-muted-h mt-6 leading-relaxed" data-testid="world-no-selection-msg">
                  CLICK A REGION TO TARGET. {isSuggestionMode ? "Your superior may dismiss you." : "The horde awaits."}
                </p>
              )}

              {selected && (
                <div className="mt-6 fade-up" data-testid="region-detail-panel">
                  <p className="tac-label">TARGET</p>
                  <h3 className="font-serif-h text-2xl text-bone uppercase mt-1" data-testid="selected-region-name">
                    {selected.name}
                  </h3>
                  <div className="mt-3 space-y-2 text-xs font-mono">
                    <Row k="CONTROLLED BY" v={
                      selected.is_king_fortress ? "THE KING"
                      : selected.controller === "ai_prince" ? "RIVAL PRINCE"
                      : selected.controller.toUpperCase()
                    } />
                    {selected.human_garrison > 0 && <Row k="HUMAN GARRISON" v={selected.human_garrison.toLocaleString()} />}
                    {selected.hostage_humans > 0 && <Row k="HOSTAGES" v={selected.hostage_humans.toLocaleString()} />}
                    {selected.zombie_force > 0 && <Row k="ZOMBIE FORCE" v={selected.zombie_force.toLocaleString()} />}
                  </div>

                  {selected.is_king_fortress ? (
                    <div className="mt-6 border-l-2 border-[#FFD700] pl-3">
                      <p className="text-xs text-[#FFD700] tracking-wider">THE KING'S FORTRESS.</p>
                      <p className="text-xs text-muted-h mt-2">
                        Cannot be assaulted directly. Trigger REBELLION at Crown Prince to challenge.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="mt-6">
                        <div className="flex items-baseline justify-between mb-2">
                          <span className="tac-label">SEND ZOMBIES</span>
                          <span className="font-mono text-bone text-xs" data-testid="send-zombies-count">{zombies}</span>
                        </div>
                        <input type="range" min="1" max={Math.max(1, state.personal_horde)} value={zombies}
                               onChange={(e) => setZombies(Number(e.target.value))}
                               className="blood-slider"
                               data-testid="send-zombies-slider" />
                      </div>

                      <button onClick={attack} disabled={busy || zombies > state.personal_horde}
                              className="btn-blood w-full mt-6" data-testid="dispatch-button">
                        {busy ? <><Loader className="w-3 h-3 animate-spin" /> ...</>
                              : isSuggestionMode ? <><Send className="w-3 h-3" /> ATTEMPT ATTACK</>
                              : <><Send className="w-3 h-3" /> DISPATCH</>}
                      </button>

                      {isSuggestionMode && (
                        <button onClick={submitSuggestion} disabled={busy || zombies > state.personal_horde}
                                className="btn-bone w-full mt-3" data-testid="suggest-button">
                          {busy ? "..." : "FORMAL SUGGESTION TO SUPERIOR"}
                        </button>
                      )}

                      {verdict && (
                        <div className={`mt-4 border-l-2 pl-3 fade-up ${
                          verdict.is_suggestion && !verdict.superior_accepts ? "border-[var(--danger)]"
                          : verdict.won === false ? "border-[var(--danger)]" : "border-[#1B7D5E]"
                        }`} data-testid="verdict-text">
                          <p className="tac-label">VERDICT</p>
                          <p className="text-sm text-bone mt-2 leading-relaxed">{verdict.verdict}</p>
                        </div>
                      )}
                    </>
                  )}

                  <button onClick={() => { setSelected(null); setVerdict(null); }}
                          className="btn-ghost mt-4 flex items-center gap-1" data-testid="clear-selection-button">
                    <X className="w-3 h-3" /> CLEAR
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Legend = ({ color, label }) => (
  <div className="flex items-center gap-2">
    <span className="w-2.5 h-2.5 inline-block" style={{ background: color }} />
    <span>{label}</span>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between border-b border-subtle pb-1">
    <span className="text-muted-h">{k}</span>
    <span className="text-bone">{v}</span>
  </div>
);

const CounterRaidPanel = ({ pending, maxHorde, onDone }) => {
  const [force, setForce] = useState(Math.max(1, Math.floor(maxHorde / 3)));
  const [busy, setBusy] = useState(false);
  const strike = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/world/counter_raid", { zombies_to_send: force });
      if (data.won) toast.success(data.verdict, { duration: 9000 });
      else toast.error(data.verdict, { duration: 9000 });
      await onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Counter-raid failed.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="narrative-panel border-l-2 border-blood mt-4 max-w-2xl"
         data-testid="counter-raid-panel">
      <p className="tac-label text-[var(--danger)]">BLOOD DEBT // {pending.prince_name}</p>
      <p className="text-sm text-bone mt-2 leading-relaxed" data-testid="counter-raid-text">
        {pending.captured
          ? `${pending.prince_name} stormed ${pending.region_name} and holds it. Strike back to reclaim what they stole.`
          : `${pending.prince_name} raided ${pending.region_name}. The dead demand an answer.`}
      </p>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <input
          type="number" min={1} max={maxHorde} value={force}
          onChange={(e) => setForce(Math.max(1, Math.min(maxHorde, parseInt(e.target.value || "1", 10))))}
          className="brutal-input !w-28"
          data-testid="counter-raid-force-input"
        />
        <button onClick={strike} disabled={busy || maxHorde < 1}
                className="btn-blood" data-testid="counter-raid-button">
          {busy ? "STRIKING..." : "STRIKE BACK"}
        </button>
        <span className="font-mono text-[0.6rem] text-muted-h tracking-widest">
          VENGEANCE: +15% POWER · ONE SHOT
        </span>
      </div>
    </div>
  );
};
